import { useCallback, useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import download from "downloadjs";
import { useSelector } from "react-redux";

import {
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  Grid,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";

import ListPage from "../../layouts/ListPage";
import PDFPreview from "../../components/Poster/PDFPreview";
import FormInput from "../../components/shared/FormInput";
import { chooseInputComponent } from "../../common/utils/components";
import { generateBeefMerchandisingPDF } from "../../utils/poster/beefPdfGenerator";
import { CARD_CONFIG } from "../../utils/poster/pdfConstants";
import { getTranslation } from "../../utils/translate";
import { languages } from "../../utils/translate";

const MAX_CUTS = CARD_CONFIG.MAX_CARDS;

const TAB_OVERVIEW = 0;
const TAB_DETAILS = 1;
const TAB_CUTS = 2;

function TabPanel({ children, value, index }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`poster-tabpanel-${index}`}
      aria-labelledby={`poster-tab-${index}`}
    >
      {value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `poster-tab-${index}`,
    "aria-controls": `poster-tabpanel-${index}`,
  };
}

function defaultPosterTitle(retailer) {
  return (
    retailer?.retailer_name ||
    retailer?.title ||
    retailer?.meta ||
    "Beef merchandising"
  );
}

function BeefMerchandisingPosterDetail() {
  const retailer = useSelector((state) => state.retailer);
  const [language, setLanguage] = useState(languages[0]);
  const [tab, setTab] = useState(TAB_OVERVIEW);
  const [posterTitle, setPosterTitle] = useState("");
  const [posterHeaderImage, setPosterHeaderImage] = useState(null);
  const [posterDefaultsReady, setPosterDefaultsReady] = useState(false);

  const [selectedBeefCutIds, setSelectedBeefCutIds] = useState([]);
  const [fileUrl, setFileUrl] = useState();
  const [pdfBytes, setPdfBytes] = useState();
  const [loading, setLoading] = useState(false);

  const { data: beefCuts, isLoading } = useSWR({
    url: `beef_product`,
    fields: [
      "*",
      "id",
      "translations.*",
      "food_service_configurations.*",
      "primal_configurations.*",
    ],
    limit: -1,
    filter: {
      status: {
        _eq: "published",
      },
    },
  });

  useEffect(() => {
    if (!retailer?.id || posterDefaultsReady) return;
    setPosterTitle(defaultPosterTitle(retailer));
    setPosterHeaderImage(retailer.logo ?? null);
    setPosterDefaultsReady(true);
  }, [retailer, posterDefaultsReady]);

  const posterImageField = useMemo(
    () => ({
      name: "posterHeaderImage",
      label: "Poster header image (center)",
      description:
        "Logo shown in the center of the poster header band. Upload to override the retailer logo.",
      type: "image",
      value: posterHeaderImage,
      handleInputChange: (_field, assetId) => {
        setPosterHeaderImage(assetId);
      },
    }),
    [posterHeaderImage],
  );

  const toggleCut = useCallback((id) => {
    setSelectedBeefCutIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((x) => x !== id);
      }
      if (prev.length >= MAX_CUTS) {
        return prev;
      }
      return [...prev, id];
    });
  }, []);

  const generatePdf = useCallback(async () => {
    if (!beefCuts?.length) return;
    setLoading(true);
    try {
      const { fileURL, pdfBytes: bytes } = await generateBeefMerchandisingPDF(
        selectedBeefCutIds,
        beefCuts,
        retailer,
        language.code,
        posterDefaultsReady
          ? {
              headerCenterLogoAssetId: posterHeaderImage,
              posterTitle: posterTitle.trim(),
            }
          : {},
      );
      setFileUrl(fileURL);
      setPdfBytes(bytes);
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      setLoading(false);
    }
  }, [
    beefCuts,
    language.code,
    posterDefaultsReady,
    posterHeaderImage,
    posterTitle,
    retailer,
    selectedBeefCutIds,
  ]);

  const handleDownload = () => {
    if (!pdfBytes) return;
    const base =
      posterTitle
        .trim()
        .replace(/[^\w\s-]/g, "")
        .replace(/\s+/g, "_")
        .slice(0, 80) || "beef_merchandising";
    const pdfBlob = new Blob([pdfBytes], { type: "application/pdf" });
    download(pdfBlob, base, "application/pdf");
  };

  const apiBase = process.env.REACT_APP_API_URL || "";

  const generateDisabled =
    loading ||
    selectedBeefCutIds.length === 0 ||
    !posterDefaultsReady;

  return (
    <ListPage
      title="Beef merchandising poster"
      description="Set the poster title and header image, then choose beef cuts."
      breadcrumbsLinks={[
        { url: "/", content: "Content Library" },
        { url: "/posters", content: "Posters" },
      ]}
      language={language}
      setLanguage={setLanguage}
    >
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        aria-label="poster builder tabs"
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        <Tab label="Overview" {...a11yProps(TAB_OVERVIEW)} />
        <Tab label="Poster details" {...a11yProps(TAB_DETAILS)} />
        <Tab
          label={`Beef cuts (${selectedBeefCutIds.length}/${MAX_CUTS})`}
          {...a11yProps(TAB_CUTS)}
        />
      </Tabs>

      <TabPanel value={tab} index={TAB_OVERVIEW}>
        <PDFPreview
          title={posterTitle || "Poster preview"}
          fileUrl={fileUrl}
          loading={loading}
          showEdit={false}
          onView={() => fileUrl && window.open(fileUrl)}
          onDownload={handleDownload}
        />
      </TabPanel>

      <TabPanel value={tab} index={TAB_DETAILS}>
        <Stack spacing={3} sx={{ maxWidth: 720 }}>
          <FormInput
            field="posterTitle"
            label="Poster title"
            description="Shown above the preview and used as the download file name."
            value={posterTitle}
            handleInputChange={(_field, value) => setPosterTitle(value)}
            type="text"
          />
          <Box key={posterHeaderImage || "no-asset"}>
            {chooseInputComponent(posterImageField, language.code)}
          </Box>
        </Stack>
      </TabPanel>

      <TabPanel value={tab} index={TAB_CUTS}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select up to {MAX_CUTS} cuts. Click a card to add or remove it from the poster.
        </Typography>

        {isLoading ? (
          <Typography sx={{ mt: 2 }}>Loading beef products…</Typography>
        ) : null}
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
          <Button
            className="form-button"
            variant="contained"
            onClick={generatePdf}
            disabled={generateDisabled}
          >
            {loading ? "Generating…" : "Generate preview"}
          </Button>
        </Stack>

        <Grid container spacing={2}>
          {(beefCuts || []).map((cut) => {
            const translated = getTranslation(
              cut,
              { key: "languages_id", code: language.code },
              "translations",
            );
            const name =
              translated?.name || cut.translations?.[0]?.name || "—";
            const selected = selectedBeefCutIds.includes(cut.id);
            const img = cut.image
              ? `${apiBase}/assets/${cut.image}?key=brand-portal-image-card-square`
              : null;
            return (
              <Grid key={cut.id} item xs={6} sm={4} md={3} lg={2}>
                <Card
                  variant="outlined"
                  sx={{
                    borderWidth: 2,
                    borderColor: selected ? "primary.main" : "divider",
                    height: "100%",
                  }}
                >
                  <CardActionArea onClick={() => toggleCut(cut.id)}>
                    <Box
                      sx={{
                        position: "relative",
                        width: "100%",
                        pt: "100%",
                        bgcolor: "#f5f5f5",
                      }}
                    >
                      {img ? (
                        <Box
                          component="img"
                          alt=""
                          src={img}
                          sx={{
                            position: "absolute",
                            inset: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                          }}
                        />
                      ) : null}
                      {selected ? (
                        <Chip
                          label="Selected"
                          color="primary"
                          size="small"
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                          }}
                        />
                      ) : null}
                    </Box>
                    <Box sx={{ p: 1.5 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          textTransform: "capitalize",
                          lineHeight: 1.3,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {name}
                      </Typography>
                    </Box>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </TabPanel>
    </ListPage>
  );
}

export default BeefMerchandisingPosterDetail;
